import { type ReactNode, createContext, useCallback, useContext, useState } from 'react';
import settings from '../config/settings';
import callUnchecked from '../utils/callUnchecked';

/** The sidebar: open, closed, or open from the lg breakpoint on (CoreUI 3's `show`; see Sidebar.utils). */
export type TShowSidebar = boolean | 'responsive';

interface IAppContextProviderProps {
  children: ReactNode;
  locale: string;
}

export interface IAppContextValue {
  /** Only in the default value: the provider passes its props in `value`. */
  locale?: string;
  value?: Omit<IAppContextProviderProps, 'children'>;
  showSidebar: TShowSidebar;
  setShowSidebar?: (showSidebar: TShowSidebar) => void;
}

export const AppContext = createContext<IAppContextValue>({
  locale: settings.defaultLocale,
  showSidebar: 'responsive',
});

const SHOWN_ON_DESKTOP: TShowSidebar[] = [true, 'responsive'];
const HIDDEN_ON_MOBILE: TShowSidebar[] = [false, 'responsive'];

export const useSidebar = () => {
  const { showSidebar, setShowSidebar } = useContext(AppContext);

  const toggleSidebar = useCallback(() => {
    const val = SHOWN_ON_DESKTOP.includes(showSidebar) ? false : 'responsive';
    callUnchecked(setShowSidebar, val);
  }, [setShowSidebar, showSidebar]);

  const toggleSidebarMobile = useCallback(() => {
    const val = HIDDEN_ON_MOBILE.includes(showSidebar) ? true : 'responsive';
    callUnchecked(setShowSidebar, val);
  }, [setShowSidebar, showSidebar]);

  /** Takes over a visibility CoreUI changed itself (see Sidebar.utils showSidebarAfterVisibleChange). */
  const syncSidebar = useCallback(
    (next: TShowSidebar) => {
      if (next !== showSidebar) setShowSidebar?.(next);
    },
    [setShowSidebar, showSidebar],
  );

  return { showSidebar, toggleSidebar, toggleSidebarMobile, syncSidebar };
};

export const AppContextProvider = ({ children, ...value }: IAppContextProviderProps) => {
  const [showSidebar, setShowSidebar] = useState<TShowSidebar>('responsive');
  return (
    // Bug: the props are passed nested in `value`, so consumers reading `locale` get undefined; kept as it was.
    <AppContext.Provider value={{ value, showSidebar, setShowSidebar }}>
      {children}
    </AppContext.Provider>
  );
};
export const AppContextConsumer = AppContext.Consumer;

export const useAppContext = () => useContext(AppContext);
